/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/

import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import isNumber from 'lodash.isnumber';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import pickBy from 'lodash.pickby';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {
  BaseSearchFilters,
  GenericObject,
  PlatformDeviceType,
  PlatformDeviceTypes,
  PlatformFileType
} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {dateFormatter, timeDiff} from '@ion/core/src/utils/DateUtil';
import {
  addSlashesToQuery,
  hasAsterisk,
  isQueryEndsWithAsterisk,
  validateQuery
} from '@ion/core/src/utils/DeviceUtils';
import {hasSpecialChars, isWhiteSpace} from '@ion/core/src/utils/StringUtil';
import {
  AttributeOperators,
  AttributeTypes,
  DateQuery,
  DynamicDataItem,
  IDatePickerState,
  InvalidDateErrorType,
  Item,
  Mapping,
  Mappings,
  MenuItem,
  SearchQueryValue,
  SearchScopeValues,
  SuggestionsType
} from './types';

export const PRIMARY_ID_SEARCH_LIMIT = 100;
export const MAX_IDENTIFIER_SEARCH_LIMIT = 1000;
export const IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT = 10000;

export type IsIdentifierLimitExceedParams = {
  queryValue: string;
  hasLargeMaxLimit?: boolean;
  hasIDMaxLimit?: boolean;
  hasAnyField?: boolean
};

/**
 * Defines function that returns page name from url
 * @param url string
 * @returns string
 *
 * ```typescript
 * import getPageNameFromUrl from '@ion/features/src/search/';
 * getPageNameFromUrl('/admin/applications') => applications
 * ```
 */
export const getPageNameFromUrl = (url: string) => {
  const pageName = url.match(/\/admin\/([a-z]*)/);
  return pageName?.length === 2
    ? pageName[1]
    : '';
};

/**
 * Defines function that returns attributes with non null values.
 * @param queries {[key: string]: any}
 * @returns filters {[key: string]: string|number}
 *
 * ```typescript
 * import getSearchableFilters from '@ion/features/src/search/';
 * getSearchableFilters({ name: null, status: "Enabled"}) => {status: "Enabled"}
 * ```
 */
export const getSearchableFilters = (queries: {[key: string]: any}) => {
  const filters = {};
  if (!isEmpty(queries)) {
    Object.keys(queries).forEach((attribute: string) => {
      if (queries[attribute]) {
        filters[attribute] = queries[attribute];
      }
    });
  }
  return filters;
};

/**
 * Defines function that determines if an object is of type Mapping
 * @param queries {[key: string]: any}
 * @returns true/false
 *
 * ```typescript
 * import isMapping from '@ion/features/src/search/';
 * isMapping({ label: 'Lame', attribute: "CONTAINS", key: "name"}) => true
 * isMapping({ label: 'Lame', key: "name"}) => false
 * ```
 */
export const isMapping = (object: any): object is Mapping =>
  !isNil(object) && ('attributes' in object && 'label' in object);

/**
 * Defines function that determines if an object is of type SearchQueryValue
 * @param queries {[key: string]: any}
 * @returns true/false
 *
 * ```typescript
 * import instanceOfSearchQueryValue from '@ion/features/src/search/';
 * instanceOfSearchQueryValue({ id: 1, name: "name"}) => true
 * instanceOfSearchQueryValue({ id: 1}) => false
 * ```
 */
export const isSearchQueryValue = (object: any): object is SearchQueryValue =>
  !isNil(object) && ('name' in object && 'id' in object);

/**
 * Defines function that determines if an object is of type SearchQueryValue
 * @param state {[key: string]: any}
 * @returns true/false
 *
 * ```typescript
 * import isDatePickerState from '@ion/features/src/search/';
 * isDatePickerState({ open: 1, value: "name"}) => true
 * isDatePickerState({ open: 1}) => false
 * ```
 */
export const isDatePickerState = (object: any): object is IDatePickerState =>
  !isNil(object) && ('open' in object && 'value' in object);

/**
 * Defines function that determines if an object is of type SearchQueryValue
 * @param value {[key: string]: any}
 * @returns true/false
 *
 * ```typescript
 * import isDateQuery from '@ion/features/src/search/';
 * isDateQuery({ label: 1, value: "name"}) => true
 * isDateQuery({ label: 1}) => false
 * ```
 */
export const isDateQuery = (object: any): object is DateQuery =>
  !isNil(object) && ('label' in object && 'value' in object);

/**
 * Defines function that determines if an Array is of type SearchQueryValue[]
 * @param value [{[key: string]: any}]
 * @returns true/false
 *
 * ```typescript
 * import isDateQueryArray from '@ion/features/src/search/';
 * isDateQueryArray([{ label: 1, value: "name"}]) => true
 * isDateQueryArray([]) => false
 * ```
 */
export const isDateQueryArray = (object: any): object is DateQuery[] =>
  !isEmpty(object) && isObject(object[0]) && ('label' in object[0] && 'value' in object[0]);

/**
 * Defines function that return a filtered results by searched parameter
 * @param dataList DynamicDataItem[]
 * @param term string to filter data against
 * @param limit number of results to display
 * @returns true/false
 *
 * ```typescript
 * import filterDynamicDataList from '@ion/features/src/search/';
 * filterDynamicDataList({ id: 1, name: "name"}) => DynamicDataItem[]
 * ```
 */
// eslint-disable-next-line max-params
export const filterDynamicDataList = debounce((dataList: DynamicDataItem[], term: string, limit: number, attribute = 'name', isFormattedText: boolean) => {
  if (!isEmpty(term) && !isWhiteSpace(term)) {
    const filteredList = dataList.filter((item: DynamicDataItem) => {
      if (isFormattedText) {
        return (item[attribute].toLowerCase()).includes(term.toLowerCase());
      }
      return item[attribute].includes(term);
    });
    return limit !== -1 ? filteredList.slice(0, limit) : filteredList;
  }
  return limit !== -1 ? dataList.slice(0, limit) : dataList;
}, 200, {
  leading : true
});

/**
 * Defines function that returns dynamic data list with searchableString
 * @param dataList DynamicDataItem[]
 * ```typescript
 * import filterDynamicDataList from '@ion/features/src/search/';
 * filterDynamicDataList({ id: 1, name: "name"}) => DynamicDataItem[]
 * ```
 */
export const transformTreeSearchableData = (dataList: DynamicDataItem[]) => {
  const list =  dataList.map((item: Item) => {
    const {name, children} = item;
    let term = name;
    if (children) {
      children.forEach(({name: SubName}: Item) => {
        term += ` ${SubName}`;
      });
    }
    item.searchableString = term;
    return item;
  });
  return list;
};

/**
 * Defines a function that transforms the specified device types data into a
 * structure suitable for tree display.
 *
 * ```typescript
 * import transformDeviceTypesToTree from '@ion/features/src/search/';
 *
 * const deviceTypesTree = transformDeviceTypesToTree(deviceTypes);
 * ```
 */
export const transformDeviceTypesToTree = (deviceTypes: PlatformDeviceTypes) => {
  const transformedTypes = deviceTypes?.map(
    ({id, name, fileTypes}: PlatformDeviceType) => {
      const currentDevice = {
        children : [],
        fullName : name,
        name,
        id
      };
      const deviceType = currentDevice.name;
      const children = fileTypes?.map(({name, platformId, platformName}: PlatformFileType) => ({
        id            : platformId,
        fullName      : platformName,
        displayName   : name,
        isDeviceFiles : true,
        deviceType,
        platformId,
        fileTypes,
        name
      }));
      const deviceLevelIds = children.map(({platformId}) => platformId);
      currentDevice.id = deviceLevelIds.join(', ');
      currentDevice.children = sortByString(children, 'name');
      return currentDevice;
    }) as unknown as Item[];
  return sortByString(transformTreeSearchableData(transformedTypes), 'name');
};

/**
 * Defines function that returns suggestion item data for a selected date type
 * @param item String[]
 * @returns string ([Date String TO Date String])
 *
 * ```typescript
 * import getDateSelection from '@ion/features/src/search/';
 * getDateSelection('Last 3 days') => string[]
 * getDateSelection(['2022-01-01T00:00:00Z', '2022-01-04T23:59:59Z']) => string[]
 * ```
 */
export const getDateSelection = (item): string[] => {
  if (typeof item === 'string') {
    const [, days] = item.split(/([0-9])/);
    return [
      moment().subtract(days, 'day').format('M/D/YYYY'),
      moment().format('M/D/YYYY')
    ];
  } else {
    const [start, end] = item;
    return [
      moment(start.value).format('M/D/YYYY'),
      moment(end.value).format('M/D/YYYY')
    ];
  }
};

/**
 * Defines function return value based on Suggestions Type
 * @param source SuggestionsType
 * @param menuItem MenuItem
 * @returns menuItem || null
 *
 * ```typescript
 * import constructQueryOption from '@ion/features/src/search/';
 * constructQueryOption(SuggestionType.DATE, {
 *  label: 'Last 3 days'
 *  value: ['2022-01-01T00:00:00Z', '2022-01-04T23:59:59Z']
 * }) => MenuItem
 * constructQueryOption(SuggestionType.TEXT, {}) => null
 * ```
 */
export const constructQueryOption  = (source: SuggestionsType, menuItem: MenuItem) => {
  if (
    (source === SuggestionsType.LOOKUP && isSearchQueryValue(menuItem)) ||
    (source === SuggestionsType.DATE && isDateQuery(menuItem))
  ) {
    return menuItem;
  }
  return null;
};

/**
 * Creates a set of filters from the current modular search parameters in the state,
 * suitable for use with the "filterOn" parameter in the call to CTC Services.
 *
 * @param searchState The current modular search parameters in the state.
 * @returns Object to use with the "filterOn" parameter.
 *
 * ```typescript
 * import createModularSearchFilters from '@ion/features/src/search/';
 *
 * const filterOn = createModularSearchFilters(searchState);
 *
 * ```
 */
export const createModularSearchFilters = (searchState: BaseSearchFilters): GenericObject => ({
  ...pickBy(searchState, isString),
  ...pickBy(searchState, isNumber)
});

/**
 * Defines utility to create ellipsis
 * * ```typescript
 * import createLabelEllipsis from '@ion/features/src/search/';
 * const {isEllipsis, label: newLabel} = createLabelEllipsis(label);
 * ```
 */
export const createLabelEllipsis = (label: string) => {
  return label?.length > 32 ? {
    isEllipsis : true,
    label      : `${label.slice(0, 16)}...`
  } : {
    label,
    isEllipsis : false
  };
};

/* Defines function to check if lookup source is tree view or not
 * @param mappings {[key: string]: Mapping}
 *
 * ```typescript
 * import isTreeView from '@ion/features/src/search/';
 * isTreeView(mappings)
 * ```
 */
export const isTreeView = (mappings: {[key: string]: Mapping}) => {
  const lookUpAttribute = Object.values(mappings).filter(({type}: Mapping) => type === AttributeTypes.LOOKUP);
  return lookUpAttribute?.[0]?.isTreeView;
};

/*
 * Utility function to determine if menu item is of type SearchQueryValue
 *
 * ```typescript
 * import {isSearchQueryValueType} from '@ion/features/src/search/';
 * isSearchQueryValueType(menuItem)
 * ```
 */
export const isSearchQueryValueType = (object): object is SearchQueryValue => {
  return !!object?.isTreeView;
};

/*
 * Utility function to determine if menu item is of type string array
 *
 * ```typescript
 * import {isSearchQueryValueType} from '@ion/features/src/search/';
 * isSearchQueryValueType(menuItem)
 * ```
 */
export const isQueryStringArray = (object): object is string[] => {
  return object?.length > 0 && object.every((item) => typeof item === 'string');
};

/*
 * Utility function to verify if condition matches
 *
 * ```typescript
 * import {isQueryCondition} from '@ion/features/src/search/';
 * isQueryCondition(AttributeOperators.IS, [AttributeOperators.IS, AttributeOperators.CONTAINS])
 * ```
 */
export const isQueryCondition = (
  condition: AttributeOperators,
  ...referenceConditions: AttributeOperators[]
) => referenceConditions.includes(condition);

/**
 * Check the search term for any field search and
 * return with quoted value.
 */
export const checkAnySearchTerm = (searchTerm: string): string => {
  const trimmedValue     = searchTerm?.trim();
  const hasAsteriskValue = hasAsterisk.test(trimmedValue);
  const asteriskInSearch = hasAsteriskValue ? validateQuery(trimmedValue) : null;
  if (hasSpecialChars(trimmedValue)) {
    return hasAsteriskValue ? `"${asteriskInSearch}"` : `"${trimmedValue}"`;
  } else if (trimmedValue?.split(' ')?.length > 1) {
    return hasAsteriskValue ? asteriskInSearch : `"${trimmedValue}"`;
  }
  return hasAsteriskValue ? asteriskInSearch : trimmedValue;
};

/**
 * Check the exact match terms for configVersion and firmwareVersion
 * filters.
 */
export const hasVersionMatch = (
  matches: {isConfigVersion?: boolean, isFirmwareVersion?: boolean},
  attribute: SearchScopeValues): boolean => {
  if (matches) {
    const {isConfigVersion, isFirmwareVersion} = matches;
    return (
      (isConfigVersion && attribute === SearchScopeValues.CONFIG_VERSION) ||
      (isFirmwareVersion && attribute === SearchScopeValues.FIRMWARE_VERSION)
    );
  }
  return false;
};

/**
 * normalizeQueryValue method to create request params with OR conditions 
 * if hasNoCharacterLimit is enabled.
 */
export const normalizeQueryValue = ({
  queryArr,
  hasAnyField,
  hasExactMatch,
  isExactMatch,
  attribute,
  isSingleValue
}) => {
  return queryArr?.map((item: string) => {
    const result   = hasAnyField ? checkAnySearchTerm(item) : item;
    const exactKey = hasExactMatch ? hasVersionMatch(isExactMatch, attribute) : false;
    return isSingleValue ? validateQuery(result, false, exactKey) : result.trim();
  })?.join(' OR ') || [];
};

/*
 * Utility reduce function to create request params with OR conditions if hasNoCharacterLimit is enabled
 *
 * ```typescript
 * import {multiValueParamsFormatter} from '@ion/features/src/search/';
 * multiValueParamsFormatter(queryParam, hasNoCharacterLimit);
 * ```
 */
export const multiValueParamsFormatter = ({
  queryValue,
  attribute,
  isExactMatch,
  hasNoCharacterLimit,
  hasPartialSubString,
  hasSpecialCharacter,
  hasLargeMaxLimit,
  hasAnyField = false,
  hasCommaSeparated = false,
  hasIDMaxLimit = false,
  hasExactMatch = false
}): string => {
  const isSingleValue = !!hasPartialSubString && queryValue?.slice(-1) !== ' ' && queryValue?.indexOf(' ') <= 0;
  const hasOR         = hasORkey(queryValue);
  const hasAND        = hasANDkey(queryValue);
  if (requiresTransformations(hasAnyField, !isSingleValue) && (hasOR || hasAND)) {
    queryValue = isQueryEndsWithAsterisk(queryValue) ? addSlashesToQuery(queryValue) : queryValue;
    return getQueryValue(queryValue);
  }
  if (!hasSpecialCharacter) {
    let queryArr = queryValue?.split(',') || [];
    const end    = (
      !hasLargeMaxLimit ? (
        hasIDMaxLimit ? PRIMARY_ID_SEARCH_LIMIT : MAX_IDENTIFIER_SEARCH_LIMIT
      ) : IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT
    );

    queryArr = isIdentifierLimitExceed(
      queryValue,
      hasLargeMaxLimit,
      hasIDMaxLimit
    ) ? queryArr.slice(0, end) : queryArr;

    if (hasNoCharacterLimit) {
      return queryValue ? normalizeQueryValue({
        queryArr,
        hasAnyField,
        hasExactMatch,
        isExactMatch,
        attribute,
        isSingleValue
      }) : '';
    }
    if (hasCommaSeparated || hasIDMaxLimit) {
      return queryValue;
    }
  } else {
    return `*${queryValue}*`;
  }
  return queryValue?.length ? validateQuery(queryValue, false) : '';
};

/*
 * Utility to check the type
 *
 * ```typescript
 *   import {isSuggestionType} from '@ion/features/src/search/';
 *
 *   isSuggestionType(type, SuggestionsType.LOOKUP, SuggestionsType.DATE);
 * ```
 */
export const isSuggestionType = (type: SuggestionsType | string, suggestions: SuggestionsType[]): boolean => {
  return suggestions.some(suggestionsType => suggestionsType === type);
};

/*
 * Utility to have any suggestion types
 *
 * ```typescript
 *   import {isAnySuggestionType} from '@ion/features/src/search/';
 *
 *   isAnySuggestionType(SuggestionsType.LOOKUP, SuggestionsType.DATE)(type);
 * ```
 */
export const isAnySuggestionType = (...suggestionsTypes: SuggestionsType[]) => {
  return (type: SuggestionsType | string) => isSuggestionType(type, suggestionsTypes);
};

/*
 * Utility function to verify if search identifier exceeds the limit
 *
 * ```typescript
 * import {isIdentifierLimitExceed} from '@ion/features/src/search/';
 * isIdentifierLimitExceed(queryValue)
 * ```
 */
export function isIdentifierLimitExceed(
  queryValueOrParams: string | IsIdentifierLimitExceedParams,
  hasLargeMaxLimit = false,
  hasIDMaxLimit = false
): boolean {
  let queryValue: string;
  let regex: RegExp = /[ ,\t]+/;
  let hasAnyField = false;

  if (queryValueOrParams && !isString(queryValueOrParams)) {
    const queryParams = queryValueOrParams as IsIdentifierLimitExceedParams;
    queryValue        = queryParams.queryValue;
    hasLargeMaxLimit  = queryParams.hasLargeMaxLimit || false;
    hasIDMaxLimit     = queryParams.hasIDMaxLimit || false;
    hasAnyField       = queryParams.hasAnyField || false;
    if (hasAnyField) {
      if ((queryValue?.includes(',')) && /\t/.test(queryValue)) {
        regex = /[,\t]+/;
      } else if (queryValue?.includes(',')) {
        regex = /[,]+/;
      } else if (/\t/.test(queryValue)) {
        regex = /\t/;
      } else {
        regex = /[ ]+/;
      }
    }
    if (hasAnyField && (hasORkey(queryValue) || hasANDkey(queryValue))) {
      regex = /[ ,OR|AND]+/g;
    }
  } else {
    queryValue = queryValueOrParams as string;
    regex = /[ ,\t]+/;
  }
  const queryArr    = queryValue?.split(regex) || [];
  const identifiers = queryArr.filter(id => !isEmpty(id));
  return identifiers.length > (
    hasLargeMaxLimit ? IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT
      : hasIDMaxLimit ? PRIMARY_ID_SEARCH_LIMIT
        : MAX_IDENTIFIER_SEARCH_LIMIT
  );
};

export const getValue = (value) => value?.map((label) => label?.label ?? label)?.join(', ');

export const getSelectedChipValue = (value: any, attribute: string): {query: any, option: any} => {
  const isStringVal = isString(value);
  const arrayVal = Array.isArray(value)
    ? getValue(value)
    : value;

  const isDateValue = !isStringVal && !Array.isArray(value) ? isDateQuery(value) : null;
  const stringArrayQuery  = !isStringVal ? value?.name || arrayVal : value;
  const stringArrayOption = !isStringVal ? value : null;
  let query               = isDateValue ? value?.label : stringArrayQuery;
  const option            = isDateValue ? value?.value : stringArrayOption;
  if (attribute.toLocaleLowerCase() === 'any' && query?.match(/(AND|OR)/gi)?.length > 0) {
    query = query?.replaceAll(',', ' ');
  }
  return {
    query,
    option
  };
};

/*
 * Utility function to remove comma from the beginning and end, trim the value
 * and split the value in case of spiltSpaces is true.
 *
 * ```typescript
 * import {removeEndSeperators} from '@ion/features/src/search/';
 * removeEndSeperators(' ,,'); ''
 * ```
 */
export const removeEndSeperators = (value: string, splitSpaces: boolean = false) => {
  if (value?.length) {
    const splitValue = value.trim().split(',');
    return (splitValue.length > 1 ? splitValue : splitSpaces ? value.split(' ') : splitValue)
      .filter((item: string) => item.length > 0)
      .map((item: string) => item.trim())
      .join(',');
  }
  return value;
};

/*
 * Utility function to check value from Checkbox.
 *
 * ```typescript
 * import {isChecked} from '@ion/features/src/search/';
 * isChecked(suggestions, list);
 * ```
 */
export const isChecked = (suggestions: string[], list: string[], isIndeterminate: boolean) => {
  return isIndeterminate ? suggestions?.toString().split(',').length !== list?.length
    : suggestions?.toString().split(',').length === list?.length;
};

/**
 * Check the OR/or key is exist in the search term
 * and return boolean if it exists.
 */
export const hasORkey = (searchTerm: string): boolean => {
  return searchTerm?.match(/( OR)/gi)?.length > 0;
};

/**
 * Check the AND/and key is exist in the search term
 * and return boolean if it exists.
 */
export const hasANDkey = (searchTerm: string): boolean => {
  return searchTerm?.match(/( AND)/gi)?.length > 0;
};

/**
 * Check the search term has both OR/AND operator
 */
export const hasBothDelimiters = (searchTerm: string): boolean => {
  return hasORkey(searchTerm) && hasANDkey(searchTerm);
};

/**
 * Check the search term has -> or/Or/oR and replace with OR
 * * key and return the search term.
 */
export const replaceORkey = (searchTerm: string): string => {
  return searchTerm?.replaceAll(/ or| oR| Or| OR/gi, 'OR');
};

/**
 * Check the search term has -> and| And| ANd| anD| aNd| aND and replace with AND
 * key and return the search term.
 */
export const replaceANDkey = (searchTerm: string): string => {
  return searchTerm?.replaceAll(/ and| And| ANd| anD| aNd| aND| aNd| AND,/gi, 'AND');
};

/**
 * Check the search term and replace the delimiter
 * and return.
 */
export const replaceSearchTerm = ({searchTerm, hasORAND, hasOnlyOR, hasOnlyAND}): string => {
  let modifiedQuery = '';
  if (hasORAND) {
    modifiedQuery = replaceORkey(searchTerm);
    modifiedQuery = replaceANDkey(modifiedQuery);
  } else if (hasOnlyOR) {
    modifiedQuery = replaceORkey(searchTerm);
  } else if (hasOnlyAND) {
    modifiedQuery = replaceANDkey(searchTerm);
  }
  return modifiedQuery;
};

/**
 * Get query value when the search term has OR/AND key.
 */
export const getQueryValue = (searchTerm: string): string => {
  const hasORAND     = hasBothDelimiters(searchTerm);
  const hasOnlyOR    = hasORkey(searchTerm);
  const hasOnlyAND   = hasANDkey(searchTerm);
  const modifiedTerm = replaceSearchTerm({
    searchTerm,
    hasORAND,
    hasOnlyOR,
    hasOnlyAND
  });
  const queryArr      = modifiedTerm?.split(/[ ,OR|AND]+/g) || [];
  const delimiterList = modifiedTerm?.match(/(AND|OR)/g);
  const searchTerms   = queryArr?.map((item) => item);
  const count         = searchTerms?.length - 1;
  if (hasORAND) {
    const items = [];
    searchTerms?.forEach((item: string, index: number) => {
      const delimiter = delimiterList?.[index] ? delimiterList[index] : 'OR';
      item            = checkAnySearchTerm(item);
      items.push(index !== count ? `${item} ${delimiter}` : item);
    });
    return items?.join(' ');
  } else if (hasOnlyOR) {
    return searchTerms?.join(' OR ');
  } else if (hasOnlyAND) {
    return searchTerms?.join(' AND ');
  }
};

/**
 * isValidLogicalSearch check the search term has start or end with any logical keys like
 * OR/AND considered as invalid query.
 */
export const isValidLogicalSearch = ({query}: GenericObject = {}): boolean => {
  if (query && query?.match(/(OR|AND)/g)?.length > 0) {
    const prefix             = /^(OR|AND)/i;
    const suffix             = /(OR|AND|,| )$/i;
    const prefixWithAsterisk = /^\*(OR|AND)|(OR|AND)\*/i;
    const suffixWithAsterisk = /\*(OR|AND)|(OR|AND)\*$/i;
    const hasAsterisk = /\*/;
    if (hasAsterisk.test(query)) {
      if (prefixWithAsterisk.test(query) || suffixWithAsterisk.test(query)) {
        return true;
      }
      return false;
    }
    return !(prefix.test(query) || suffix.test(query));
  }
  return true;
};

/**
 * Filters an entity mapping and returns only those entries where the value contains a `label` property.
 *
 * This function takes an object (`entityMappings`) where the keys are strings and the values are objects.
 * It checks each value to see if it contains a `label` property. If the `label` exists, the key-value pair is
 * added to the result. Otherwise, the entry is excluded from the returned object.
 *
 * @param entityMappings - An object where each key maps to an entity with an optional `label` property.
 * 
 * @returns A new object containing only the entries from `entityMappings` where the value has a `label` property.
 *          If none of the values have a `label`, an empty object is returned.
 *
 * ```typescript
 * 
 * import {validateEntityMap} from './utils';
 * 
 * const entities = {
 *   item1: {label: 'Entity 1', value: 10},
 *   item2: {value: 20 },
 *   item3: {label: 'Entity 3', value: 30}
 * };
 *
 * const validEntities = validateEntityMap(entities);
 * // validEntities is now:
 * // {
 * //   item1: {label: 'Entity 1', value: 10},
 * //   item3: {label: 'Entity 3', value: 30}
 * // }
 * 
 * ```
 */
export const validateEntityMap = (entityMappings: Mappings = {}): Mappings => (
  Object.entries(entityMappings).reduce((valid, [key, value]) => {
    if ((value as any)?.label) valid[key] = value;
    return valid;
  }, {})
);

/**
 * Defines the specific pages for which specific transformations are to be
 * made for queries.
 */
const transformationTargetPages = {
  devices : true
};

/**
 * Determines if specific transformations are to be made for queries based
 * on the current page and provided arguments.
 */
export const requiresTransformations = (anyField: boolean, isMultiple: boolean): boolean => {
  const currentPage  = globalThis?.location?.pathname?.replace('/', '');
  const isTargetPage = transformationTargetPages[currentPage];
  return isTargetPage && anyField && isMultiple;
};

/**
 * datePickerDateValidations check the validations of Start Date and End Date
 * and returns the validatation status and error type.
 */
export const datePickerDateValidations = (start, end) => {
  if (isNull(start) && isNull(end)) {
    return {isValid: false, errorType: InvalidDateErrorType.EMPTY_DATES};
  } else if (isNull(start) && !isNull(end)) {
    return {isValid: false, errorType: InvalidDateErrorType.EMPTY_START_DATE};
  } else if (!isNull(start) && isNull(end)) {
    return {isValid: false, errorType: InvalidDateErrorType.EMPTY_END_DATE};
  } else {
    const daysDiff = timeDiff(dateFormatter(end), dateFormatter(start));
    return {isValid: daysDiff >= 0, errorType: InvalidDateErrorType.INVALID_DATES};
  }
};

/**
 * getInvalidDateErrorMessages compares the error type based on that
 * it will returns error message.
 */
export const getInvalidDateErrorMessages = (msg) => {
  if (msg === InvalidDateErrorType.EMPTY_DATES) {
    return translate('common:time.emptyDates');
  } else if (msg === InvalidDateErrorType.EMPTY_START_DATE) {
    return translate('common:time.emptyStartDate');
  } else if (msg === InvalidDateErrorType.EMPTY_END_DATE) {
    return translate('common:time.emptyEndDate');
  } else {
    return translate('common:time.invalidEndDate');
  }
};
